/**
 * ma-animations.js — majidalyousef.com
 * GSAP 3 + ScrollTrigger animation layer
 * Loaded via layout/theme.liquid (defer, after GSAP CDN scripts)
 */

(function () {
  'use strict';

  // Skip in Shopify theme editor to avoid interfering with live preview
  if (window.Shopify && window.Shopify.designMode) return;

  // Respect user preference for reduced motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Poll until GSAP + ScrollTrigger are ready (CDN scripts load async)
  function init() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(init, 40);
      return;
    }
    gsap.registerPlugin(ScrollTrigger);
    setupAnimations();
  }

  function setupAnimations() {

    // ─── PAGE TRANSITION OVERLAY ──────────────────────────────────────────────
    var veil = document.createElement('div');
    veil.id = 'ma-page-veil';
    veil.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:9999',
      'background:#fff',
      'pointer-events:none',
      'opacity:1',
    ].join(';');
    document.body.appendChild(veil);

    gsap.to(veil, {
      opacity: 0,
      duration: 0.45,
      ease: 'power1.out',
      delay: 0.05,
    });

    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href');
      if (!href) return;

      if (
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('javascript:') ||
        link.target === '_blank' ||
        link.hasAttribute('download')
      ) return;

      try {
        var url = new URL(href, window.location.href);
        if (url.hostname !== window.location.hostname) return;
        if (
          url.pathname.startsWith('/cart/') ||
          url.pathname.startsWith('/checkout') ||
          url.pathname.startsWith('/account/login') ||
          url.pathname.startsWith('/account/register')
        ) return;
      } catch (err) { return; }

      if (
        link.closest('[data-ajax-cart]') ||
        link.closest('predictive-search') ||
        link.closest('[data-no-transition]') ||
        link.getAttribute('data-ajax') === 'true'
      ) return;

      e.preventDefault();
      veil.style.pointerEvents = 'auto';

      gsap.to(veil, {
        opacity: 1,
        duration: 0.28,
        ease: 'power1.in',
        onComplete: function () {
          window.location.href = href;
        },
      });
    });

    // ─── HEADER ENTRANCE ──────────────────────────────────────────────────────
    gsap.from('.ma-header-wrapper', {
      y: -8,
      opacity: 0,
      duration: 0.55,
      ease: 'power2.out',
      delay: 0.35,
      clearProps: 'all',
    });

    var brandMark = document.querySelector('.ma-brand__mark');
    if (brandMark) {
      gsap.from(brandMark, {
        scale: 0.72,
        opacity: 0,
        duration: 0.65,
        ease: 'back.out(1.5)',
        delay: 0.4,
        transformOrigin: 'center center',
        clearProps: 'all',
      });
    }

    var brandName = document.querySelector('.ma-brand__name');
    if (brandName) {
      gsap.from(brandName, {
        opacity: 0,
        x: -6,
        duration: 0.45,
        ease: 'power2.out',
        delay: 0.52,
        clearProps: 'all',
      });
    }

    gsap.from('.ma-header__icons > *', {
      opacity: 0,
      x: 6,
      duration: 0.4,
      ease: 'power2.out',
      stagger: 0.06,
      delay: 0.45,
      clearProps: 'all',
    });

    // ─── MAIN CONTENT ENTRANCE ────────────────────────────────────────────────
    gsap.from('#MainContent', {
      opacity: 0,
      y: 14,
      duration: 0.65,
      ease: 'power2.out',
      delay: 0.3,
      clearProps: 'all',
    });

    // ─── SCROLL REVEALS – SECTIONS ────────────────────────────────────────────
    var sectionSelectors = [
      '.ma-box',
      '.ma-blog',
      '.banner',
      '.content-for-layout > .shopify-section > *',
    ];

    sectionSelectors.forEach(function (sel) {
      gsap.utils.toArray(sel).forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.5) return;

        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 90%',
            once: true,
          },
          opacity: 0,
          y: 28,
          duration: 0.7,
          ease: 'power2.out',
          clearProps: 'all',
        });
      });
    });

    // ─── SCROLL REVEALS – PRODUCT / COLLECTION CARD GRIDS ────────────────────
    var cardGrids = document.querySelectorAll(
      '.product-grid, .collection-grid, ul.grid, .grid'
    );

    cardGrids.forEach(function (grid) {
      var cards = Array.from(
        grid.querySelectorAll('.grid__item, .card-wrapper, li.grid__item, .ma-card')
      );
      if (cards.length < 2) return;

      gsap.from(cards, {
        scrollTrigger: {
          trigger: grid,
          start: 'top 88%',
          once: true,
        },
        opacity: 0,
        y: 22,
        duration: 0.55,
        ease: 'power2.out',
        stagger: {
          each: 0.08,
          from: 'start',
        },
        clearProps: 'all',
      });
    });

    // ─── SCROLL REVEALS – SECTION HEADERS & TITLES ───────────────────────────
    gsap.utils.toArray('.ma-box-head, .section__header').forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.6) return;
      gsap.from(el, {
        scrollTrigger: { trigger: el, start: 'top 90%', once: true },
        opacity: 0,
        y: 16,
        duration: 0.55,
        ease: 'power2.out',
        clearProps: 'all',
      });
    });

    // ─── DRAWER – ENHANCED OPEN / CLOSE ──────────────────────────────────────
    var drawer = document.getElementById('ma-drawer');
    if (drawer) {
      var drawerObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName !== 'class') return;

          if (drawer.classList.contains('is-open')) {
            gsap.killTweensOf(drawer);
            var links = drawer.querySelectorAll(
              '.ma-drawer__link, .ma-drawer__sublink'
            );
            gsap.from(links, {
              x: -14,
              opacity: 0,
              duration: 0.3,
              ease: 'power2.out',
              stagger: 0.045,
              delay: 0.06,
              clearProps: 'all',
            });
            var newsletter = drawer.querySelector('.ma-drawer__newsletter');
            if (newsletter) {
              gsap.from(newsletter, {
                opacity: 0,
                y: 8,
                duration: 0.35,
                ease: 'power2.out',
                delay: 0.25,
                clearProps: 'all',
              });
            }
          } else {
            gsap.killTweensOf(drawer);
          }
        });
      });

      drawerObserver.observe(drawer, { attributes: true });
    }

    // ─── SEARCH OVERLAY – PANEL ENTRANCE ─────────────────────────────────────
    var searchOverlay = document.getElementById('ma-search-overlay');
    if (searchOverlay) {
      var searchPanel = searchOverlay.querySelector('.ma-search-overlay__panel');

      var searchObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName !== 'class') return;
          if (searchOverlay.classList.contains('is-open') && searchPanel) {
            gsap.from(searchPanel, {
              y: -12,
              opacity: 0,
              duration: 0.3,
              ease: 'power2.out',
              clearProps: 'all',
            });
          }
        });
      });

      searchObserver.observe(searchOverlay, { attributes: true });
    }

    // ─── BUTTON CLICK FEEDBACK (scale pulse) ─────────────────────────────────
    document.addEventListener('mousedown', function (e) {
      var btn = e.target.closest(
        '.button, .ma-blog__cta, .ma-shop-btn, .ma-btn, .ma-drawer__nl-btn'
      );
      if (!btn) return;
      gsap.fromTo(
        btn,
        { scale: 1 },
        {
          scale: 0.95,
          duration: 0.08,
          ease: 'power1.in',
          onComplete: function () {
            gsap.to(btn, {
              scale: 1,
              duration: 0.35,
              ease: 'elastic.out(1.3, 0.45)',
              clearProps: 'scale',
            });
          },
        }
      );
    });

    // ─── HAMBURGER ICON – MORPH LINES ON TOGGLE ───────────────────────────────
    var hamburger = document.getElementById('ma-hamburger-btn');
    if (hamburger) {
      var spans = hamburger.querySelectorAll('span');
      if (spans.length === 3) {
        var hamObserver = new MutationObserver(function () {
          if (hamburger.classList.contains('is-active')) {
            gsap.to(spans[0], { y: 6,  rotation: 45,  duration: 0.25, ease: 'power2.inOut', transformOrigin: 'center' });
            gsap.to(spans[1], { opacity: 0, x: -6,     duration: 0.15, ease: 'power1.in' });
            gsap.to(spans[2], { y: -6, rotation: -45, duration: 0.25, ease: 'power2.inOut', transformOrigin: 'center' });
          } else {
            gsap.to(spans[0], { y: 0, rotation: 0, duration: 0.25, ease: 'power2.inOut', clearProps: 'all' });
            gsap.to(spans[1], { opacity: 1, x: 0, duration: 0.2,  ease: 'power2.out', clearProps: 'all' });
            gsap.to(spans[2], { y: 0, rotation: 0, duration: 0.25, ease: 'power2.inOut', clearProps: 'all' });
          }
        });
        hamObserver.observe(hamburger, { attributes: true });
      }
    }

    // ─── CART DRAWER (if used) ────────────────────────────────────────────────
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      var cartObserver = new MutationObserver(function () {
        if (cartDrawer.classList.contains('is-empty') === false &&
            cartDrawer.hasAttribute('open')) {
          var inner = cartDrawer.querySelector('[id$="CartDrawer-Form"], .drawer__inner');
          if (inner) {
            gsap.from(inner, {
              x: 30,
              opacity: 0,
              duration: 0.35,
              ease: 'power2.out',
              clearProps: 'all',
            });
          }
        }
      });
      cartObserver.observe(cartDrawer, { attributes: true });
    }

    // ─── ADD TO CART – RIPPLE + SUCCESS BOUNCE ────────────────────────────────
    document.addEventListener('mousedown', function (e) {
      var cartBtn = e.target.closest('.product-form__submit');
      if (!cartBtn || cartBtn.disabled) return;

      var ripple = document.createElement('span');
      var rect   = cartBtn.getBoundingClientRect();
      var size   = Math.max(rect.width, rect.height) * 2.2;
      var x      = e.clientX - rect.left - size / 2;
      var y      = e.clientY - rect.top  - size / 2;

      ripple.style.cssText = [
        'position:absolute',
        'border-radius:50%',
        'background:rgba(255,255,255,0.28)',
        'pointer-events:none',
        'left:'  + x    + 'px',
        'top:'   + y    + 'px',
        'width:' + size + 'px',
        'height:'+ size + 'px',
      ].join(';');

      if (getComputedStyle(cartBtn).position === 'static') {
        cartBtn.style.position = 'relative';
      }
      cartBtn.style.overflow = 'hidden';
      cartBtn.appendChild(ripple);

      gsap.fromTo(ripple,
        { scale: 0, opacity: 1 },
        {
          scale: 1, opacity: 0,
          duration: 0.62,
          ease: 'power2.out',
          onComplete: function () {
            if (ripple.parentNode) ripple.parentNode.removeChild(ripple);
          },
        }
      );
    });

    document.querySelectorAll('[id^="ProductSubmitButton"]').forEach(function (btn) {
      var wasLoading = false;
      new MutationObserver(function () {
        var isLoading = btn.classList.contains('loading');
        if (wasLoading && !isLoading) {
          gsap.fromTo(btn,
            { scale: 0.96 },
            { scale: 1, duration: 0.55, ease: 'elastic.out(1.2, 0.4)', clearProps: 'scale' }
          );
        }
        wasLoading = isLoading;
      }).observe(btn, { attributes: true, attributeFilter: ['class'] });
    });

    // ─── WISHLIST HEART – ANIMATE ON CLASS CHANGE ─────────────────────────────
    function maSetupWishAnim(btn) {
      if (btn.dataset.maWishAnim) return;
      btn.dataset.maWishAnim = '1';
      var icon = btn.querySelector('svg, .icon, i') || btn;
      var wasWishlisted = btn.classList.contains('is-wishlisted');

      new MutationObserver(function () {
        var isNow = btn.classList.contains('is-wishlisted');
        if (isNow === wasWishlisted) return;
        wasWishlisted = isNow;

        gsap.killTweensOf(icon);
        if (isNow) {
          gsap.fromTo(icon,
            { scale: 0.55 },
            { scale: 1, duration: 0.55, ease: 'back.out(2.2)', clearProps: 'scale' }
          );
        } else {
          gsap.to(icon, {
            scale: 0.7, duration: 0.12, ease: 'power2.in',
            onComplete: function () {
              gsap.to(icon, { scale: 1, duration: 0.25, ease: 'power2.out', clearProps: 'scale' });
            },
          });
        }
      }).observe(btn, { attributes: true, attributeFilter: ['class'] });
    }

    document.querySelectorAll('.pcrd__wishlist-btn, .ma-card__wish')
      .forEach(maSetupWishAnim);

    // ─── PRODUCT CARD CART – GSAP FEEDBACK ───────────────────────────────────
    // Fires on is-tapping (added immediately on click) for zero-delay feedback.
    // Same back.out(2.2) bounce as the wishlist heart — clean and subtle.
    function maSetupCartAnim(btn) {
      if (btn.dataset.maCartAnim) return;
      btn.dataset.maCartAnim = '1';
      var icon = btn.querySelector('svg') || btn;
      var prevTapping = false;

      new MutationObserver(function () {
        var tapping = btn.classList.contains('is-tapping');
        if (tapping && !prevTapping) {
          gsap.killTweensOf(icon);
          gsap.fromTo(icon,
            { scale: 0.55 },
            { scale: 1, duration: 0.55, ease: 'back.out(2.2)', clearProps: 'scale' }
          );
        }
        prevTapping = tapping;
      }).observe(btn, { attributes: true, attributeFilter: ['class'] });
    }

    document.querySelectorAll('.pcrd__add-cart').forEach(maSetupCartAnim);

  } // end setupAnimations()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
